import { useState, useRef } from 'react'
import { DollarSign, Edit, Save } from 'lucide-react'
import { X as XIcon } from 'lucide-react'
import toast from 'react-hot-toast'
import { talents, libraries } from '../../services/apiService'
import { TextField, Autocomplete } from '@anyemp/ui-kit'
import SectionBlock from '../UI/SectionBlock'
import SectionHeader from '../UI/SectionHeader'

const SalarySection = ({ talent, talentId, hasPermission, setTalent }) => {
  const [isEditingSalary, setIsEditingSalary] = useState(false)
  const [salaryFormData, setSalaryFormData] = useState({
    expectations_amount: '',
    expectations_currency_id: '',
    proposed_salary_amount: '',
    proposed_salary_currency_id: '',
    shift_id: '',
    rate_id: ''
  })
  const [currencyOptions, setCurrencyOptions] = useState([])
  const [shiftOptions, setShiftOptions] = useState([])
  const [rateOptions, setRateOptions] = useState([])
  const [salaryLoading, setSalaryLoading] = useState(false)
  const [optionsLoading, setOptionsLoading] = useState(false)
  const optionsLoadedRef = useRef(false)

  const loadSalaryOptions = async () => {
    if (optionsLoadedRef.current) return
    optionsLoadedRef.current = true
    setOptionsLoading(true)
    try {
      const [currenciesResp, shiftsResp, ratesResp] = await Promise.all([
        libraries.getCurrencies(),
        libraries.getShifts(),
        libraries.getRates()
      ])
      const currencies = currenciesResp.data || currenciesResp
      const shifts = shiftsResp.data || shiftsResp
      const rates = ratesResp.data || ratesResp
      setCurrencyOptions(currencies.map(c => ({ value: String(c.id), label: `${c.name} (${c.symbol})` })))
      setShiftOptions(shifts.map(s => ({ value: String(s.id), label: `${s.name} (${s.start_time?.slice(0, 5)} - ${s.end_time?.slice(0, 5)})` })))
      setRateOptions(rates.map(r => ({ value: String(r.id), label: `${r.name} (${r.hours}h, ${r.value})` })))
    } catch (err) {
      console.error('Error loading salary options:', err)
      optionsLoadedRef.current = false
    } finally {
      setOptionsLoading(false)
    }
  }

  const handleEditSalary = () => {
    setSalaryFormData({
      expectations_amount: talent.salary?.expectations_amount || '',
      expectations_currency_id: talent.salary?.expectations_currency_id != null ? String(talent.salary.expectations_currency_id) : '',
      proposed_salary_amount: talent.salary?.proposed_salary_amount || '',
      proposed_salary_currency_id: talent.salary?.proposed_salary_currency_id != null ? String(talent.salary.proposed_salary_currency_id) : '',
      shift_id: talent.salary?.shift_id != null ? String(talent.salary.shift_id) : '',
      rate_id: talent.salary?.rate_id != null ? String(talent.salary.rate_id) : ''
    })
    setIsEditingSalary(true)
    loadSalaryOptions()
  }

  const handleCancelSalary = () => {
    setIsEditingSalary(false)
    setSalaryFormData({ expectations_amount: '', expectations_currency_id: '', proposed_salary_amount: '', proposed_salary_currency_id: '', shift_id: '', rate_id: '' })
  }

  const handleSaveSalary = async () => {
    try {
      setSalaryLoading(true)
      await talents.updateTalentSalary(talentId, {
        expectations_amount: salaryFormData.expectations_amount || null,
        expectations_currency_id: salaryFormData.expectations_currency_id ? parseInt(salaryFormData.expectations_currency_id) : null,
        proposed_salary_amount: salaryFormData.proposed_salary_amount || null,
        proposed_salary_currency_id: salaryFormData.proposed_salary_currency_id ? parseInt(salaryFormData.proposed_salary_currency_id) : null,
        shift_id: salaryFormData.shift_id ? parseInt(salaryFormData.shift_id) : null,
        rate_id: salaryFormData.rate_id ? parseInt(salaryFormData.rate_id) : null
      })
      const response = await talents.getTalentByIdWithRelations(talentId)
      if (response?.data) setTalent(response.data)
      setIsEditingSalary(false)
    } catch (err) {
      console.error('Error saving salary:', err)
      toast.error('Failed to save salary data')
    } finally {
      setSalaryLoading(false)
    }
  }

  const cardCls = 'border border-light-border-primary dark:border-dark-border-primary rounded-lg p-4 bg-light-bg-tertiary dark:bg-dark-bg-tertiary'

  return (
    <SectionBlock id="salary">
      <SectionHeader
        icon={DollarSign}
        title="Salary"
        action={!isEditingSalary && hasPermission('talent-service', `${talent.talent_type}_salary`, 'update') ? (
          <button onClick={handleEditSalary} onMouseEnter={loadSalaryOptions} onFocus={loadSalaryOptions} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors">
            <Edit className="h-3.5 w-3.5" />
            {talent.salary ? 'Edit' : 'Add'}
          </button>
        ) : null}
      />
      {isEditingSalary ? (
        <div className="space-y-4">
          <div className={cardCls}>
            <h4 className="font-medium text-sm text-light-text-primary dark:text-dark-text-primary mb-3 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-emerald-600 dark:text-emerald-400" /> Expectations
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <TextField label="Amount" size="small" fullWidth type="number" step="0.01" value={salaryFormData.expectations_amount} onChange={e => setSalaryFormData({ ...salaryFormData, expectations_amount: e.target.value })} placeholder="0.00" />
              </div>
              <div>
                <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Currency</label>
                <Autocomplete clearable fullWidth options={currencyOptions} value={salaryFormData.expectations_currency_id} onChange={value => setSalaryFormData({ ...salaryFormData, expectations_currency_id: value || '' })} placeholder={optionsLoading ? 'Loading…' : 'Select currency'} disabled={optionsLoading} />
              </div>
            </div>
          </div>
          <div className={cardCls}>
            <h4 className="font-medium text-sm text-light-text-primary dark:text-dark-text-primary mb-3 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-blue-600 dark:text-blue-400" /> Proposed Salary
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <TextField label="Amount" size="small" fullWidth type="number" step="0.01" value={salaryFormData.proposed_salary_amount} onChange={e => setSalaryFormData({ ...salaryFormData, proposed_salary_amount: e.target.value })} placeholder="0.00" />
              </div>
              <div>
                <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Currency</label>
                <Autocomplete clearable fullWidth options={currencyOptions} value={salaryFormData.proposed_salary_currency_id} onChange={value => setSalaryFormData({ ...salaryFormData, proposed_salary_currency_id: value || '' })} placeholder="Select currency" />
              </div>
            </div>
          </div>
          <div className={cardCls}>
            <h4 className="font-medium text-sm text-light-text-primary dark:text-dark-text-primary mb-3">Shift & Rate</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Shift</label>
                <Autocomplete clearable fullWidth options={shiftOptions} value={salaryFormData.shift_id} onChange={value => setSalaryFormData({ ...salaryFormData, shift_id: value || '' })} placeholder={optionsLoading ? 'Loading…' : 'Select shift'} disabled={optionsLoading} />
              </div>
              <div>
                <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Rate</label>
                <Autocomplete clearable fullWidth options={rateOptions} value={salaryFormData.rate_id} onChange={value => setSalaryFormData({ ...salaryFormData, rate_id: value || '' })} placeholder={optionsLoading ? 'Loading…' : 'Select rate'} disabled={optionsLoading} />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap justify-end gap-3">
            <button onClick={handleCancelSalary} disabled={salaryLoading} className="px-4 py-2 border border-light-border-primary dark:border-dark-border-primary rounded-lg text-sm text-light-text-primary dark:text-dark-text-primary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors disabled:opacity-50">
              <XIcon className="h-4 w-4 inline mr-2" />Cancel
            </button>
            <button onClick={handleSaveSalary} disabled={salaryLoading || optionsLoading} className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors disabled:opacity-50 flex items-center gap-2">
              {salaryLoading ? <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />Saving...</> : <><Save className="h-4 w-4" />Save</>}
            </button>
          </div>
        </div>
      ) : talent.salary ? (
        <div className="space-y-4">
          <div className={cardCls}>
            <h4 className="font-medium text-sm text-light-text-primary dark:text-dark-text-primary mb-3 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-emerald-600 dark:text-emerald-400" /> Expectations
            </h4>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Amount</span>
                <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">{talent.salary.expectations_amount ? parseFloat(talent.salary.expectations_amount).toFixed(2) : '—'}</p>
              </div>
              <div>
                <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Currency</span>
                <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">{talent.salary.expectations_currency ? `${talent.salary.expectations_currency.name} (${talent.salary.expectations_currency.symbol})` : '—'}</p>
              </div>
            </div>
          </div>
          <div className={cardCls}>
            <h4 className="font-medium text-sm text-light-text-primary dark:text-dark-text-primary mb-3 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-blue-600 dark:text-blue-400" /> Proposed Salary
            </h4>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Amount</span>
                <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">{talent.salary.proposed_salary_amount ? parseFloat(talent.salary.proposed_salary_amount).toFixed(2) : '—'}</p>
              </div>
              <div>
                <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Currency</span>
                <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">{talent.salary.proposed_salary_currency ? `${talent.salary.proposed_salary_currency.name} (${talent.salary.proposed_salary_currency.symbol})` : '—'}</p>
              </div>
            </div>
          </div>
          <div className={cardCls}>
            <h4 className="font-medium text-sm text-light-text-primary dark:text-dark-text-primary mb-3">Shift & Rate</h4>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Shift</span>
                <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">{talent.salary.shift ? `${talent.salary.shift.name} (${talent.salary.shift.start_time?.slice(0, 5)} - ${talent.salary.shift.end_time?.slice(0, 5)})` : '—'}</p>
              </div>
              <div>
                <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Rate</span>
                <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">{talent.salary.rate ? `${talent.salary.rate.name} (${talent.salary.rate.hours}h, ${talent.salary.rate.value})` : '—'}</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">No salary data configured</p>
      )}
    </SectionBlock>
  )
}

export default SalarySection
